import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import {
  LayoutDashboard,
  MessageSquare,
  Image as ImageIcon,
  Code2,
  CreditCard,
  LogOut,
  FileText,
  Gift,
  Shield,
  Briefcase,
  Bell,
  Settings as SettingsIcon,
  Users,
  Menu,
  X,
  Scale,
  BookMarked,
  FileSignature,
  Store,
} from "lucide-react";

const TIER_LABEL = { free: "FREE", starter: "STARTER", pro: "PRO", business: "BUSINESS", legal_pro: "LEGAL PRO" };

export function AppSidebar({ mobileOpen = false, onMobileClose = () => {} } = {}) {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useImperativeLoad((() => () => {
    let mounted = true;
    const load = () => {
      api.get("/activity/events", { params: { limit: 1 } })
        .then((r) => mounted && setUnread(r.data.unread_count || 0))
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 60000); // refresh every 60s
    // Stash cleanup on window — runs at next mount cycle
    return () => { mounted = false; clearInterval(id); };
  })(), []);

  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: t("common.dashboard"), testId: "nav-dashboard" },
    { to: "/chat", icon: MessageSquare, label: t("chat.new_chat"), testId: "nav-chat" },
    { to: "/images", icon: ImageIcon, label: t("images.title"), testId: "nav-images" },
    { to: "/code", icon: Code2, label: t("code.title"), testId: "nav-code" },
    { to: "/documents", icon: FileText, label: t("documents.title"), testId: "nav-documents" },
    // Jurisprudence is shown for everyone — read-only browsable corpus. Legal Pro users
    // get auto-citations in chat; others can still consult the database for reference.
    { to: "/legal/jurisprudence", icon: Scale, label: t("jurisprudence.title_nav"), testId: "nav-jurisprudence" },
    // Personal Legal Knowledge Base — gated to legal_pro / business. We still show
    // the link to everyone (the page itself renders an upsell for non-allowed tiers)
    // so the feature is discoverable.
    { to: "/legal/knowledge", icon: BookMarked, label: t("legal_kb.title_nav"), testId: "nav-legal-kb" },
    // Atto Generator — Legal Pro killer feature. Page shows an upsell card
    // for non-allowed tiers so the link can stay visible to everyone.
    { to: "/legal/atti", icon: FileSignature, label: t("atti.title_nav"), testId: "nav-atti" },
    { to: "/marketplace", icon: Store, label: t("marketplace.nav"), testId: "nav-marketplace" },
    { to: "/activity", icon: Bell, label: t("activity.title"), testId: "nav-activity", badge: unread },
    { to: "/referrals", icon: Gift, label: t("referrals.title"), testId: "nav-referrals" },
    ...(user?.is_affiliate && user?.affiliate_status === "approved"
      ? [{ to: "/partner", icon: Briefcase, label: t("partner.title"), testId: "nav-partner" }]
      : []),
    { to: "/team", icon: Users, label: t("team.nav_label"), testId: "nav-team" },
    { to: "/billing", icon: CreditCard, label: t("billing.title"), testId: "nav-billing" },
    ...(user?.is_admin
      ? [{ to: "/admin", icon: Shield, label: t("admin.title"), testId: "nav-admin" }]
      : []),
    { to: "/settings", icon: SettingsIcon, label: t("settings.title"), testId: "nav-settings" },
  ];

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={onMobileClose}
          aria-hidden
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex h-screen w-72 flex-col border-r border-slate-800 bg-[#05073B] text-slate-200 transition-transform duration-300 ease-out lg:static lg:w-64 lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="app-sidebar"
      >
        <div className="flex items-center justify-between border-b border-slate-800 px-6 py-5">
          <Logo size="md" inverted />
          <button
            type="button"
            onClick={onMobileClose}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white lg:hidden"
            aria-label="Close menu"
            data-testid="sidebar-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

      <div className="flex-1 overflow-y-auto px-4 py-6">
        <nav className="space-y-1">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testId}
              onClick={onMobileClose}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#9ACD32]/15 text-[#9ACD32]"
                    : "text-slate-300 hover:bg-slate-800/40 hover:text-white"
                }`
              }
            >
              <it.icon className="h-4 w-4" />
              <span className="flex-1">{it.label}</span>
              {it.badge > 0 && (
                <span
                  className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#9ACD32] px-1.5 text-[10px] font-bold text-[#05073B]"
                  data-testid={`${it.testId}-badge`}
                >
                  {it.badge > 99 ? "99+" : it.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </div>

      <div className="border-t border-slate-800 p-4">
        {user && (
          <div className="mb-3">
            <div className="flex items-center gap-3 rounded-lg bg-slate-800/40 p-3">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name || user.email}
                  className="h-9 w-9 rounded-full"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#9ACD32] text-sm font-bold text-[#05073B]">
                  {(user.name || user.email).charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold text-white" data-testid="sidebar-username">
                  {user.name || user.email}
                </p>
                <span className={`tier-badge tier-${user.subscription_tier} mt-0.5`} data-testid="sidebar-tier-badge">
                  {TIER_LABEL[user.subscription_tier]}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-8 w-8 rounded-full p-0 text-slate-400 hover:bg-slate-800 hover:text-white"
            onClick={async () => {
              await logout();
              navigate("/");
            }}
            data-testid="sidebar-logout-btn"
            title={t("common.sign_out")}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>

        <a
          href="https://telcavoip.ai"
          target="_blank"
          rel="noreferrer noopener"
          className="mt-3 inline-flex items-center justify-center gap-1 rounded-full border border-[#9ACD32]/25 bg-[#9ACD32]/5 px-3 py-1.5 text-[10px] font-semibold text-slate-400 transition-colors hover:border-[#9ACD32]/60 hover:text-white"
          data-testid="sidebar-ai-powered-badge"
        >
          <span>AI Powered with</span>
          <span aria-label="love" className="text-[#9ACD32]">♥</span>
          <span>by</span>
          <span className="font-bold text-[#9ACD32]">TelcaVoIP AI</span>
        </a>
      </div>
    </aside>
    </>
  );
}
